import React, { useState, useEffect, useMemo,useCallback} from 'react';
import axios from 'axios';
import { toast } from 'react-toastify';
import debounce from 'lodash/debounce';
import ReturnOrderDetail from '../../components/ReturnOrderDetail/ReturnOrderDetail';
import OrderStatusBadge from '../../components/order/OrderStatusBadge';
import styles from './ReturnOrder.module.css';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;


const filterOrders = (orders, filters, searchTerm) => {
    let result = orders;

    // Status filter
    if (filters.status === 'issuedCommittedPartiallyReturned') {
        result = result.filter(order => 
            order.orderStatus === 'issued' || 
            order.orderStatus === 'committed' || 
            order.orderStatus === 'partially_returned'
        );
    } else if (filters.status === 'issuedAndCommitted') {
        result = result.filter(order => 
            order.orderStatus === 'issued' || order.orderStatus === 'committed'
        );
    } else if (filters.status !== 'all') {
        result = result.filter(order => order.orderStatus === filters.status);
    }

    // Date filter
    if (filters.dateRange !== 'all') {
        const now = new Date();
        const daysDiff = {
            '7days': 7,
            '30days': 30,
            '90days': 90
        };
        const days = daysDiff[filters.dateRange];
        if (days) {
            result = result.filter(order => {
                const orderDate = new Date(order.createdAt);
                return (now - orderDate) / (1000 * 60 * 60 * 24) <= days;
            });
        }
    }

    // Search filter
    if (searchTerm) {
        const searchTermLower = searchTerm.toLowerCase();
        result = result.filter(order => 
            (order.orderNumber?.toString().includes(searchTermLower)) ||
            (order.destination?.toLowerCase().includes(searchTermLower)) ||
            (order.requestedByName?.toLowerCase().includes(searchTermLower)) || 
            (order.orderName?.toLowerCase().includes(searchTermLower))
        );
    }

    return result;
};

const ReturnOrder = () => {
    const [orders, setOrders] = useState([]);
    const [selectedOrder, setSelectedOrder] = useState(null);
    const [searchTerm, setSearchTerm] = useState('');
    const [isLoading, setIsLoading] = useState(true);
    const [isReturnModalOpen, setIsReturnModalOpen] = useState(false);
    const [debouncedSearch, setDebouncedSearch] = useState('');
    const [filters, setFilters] = useState({
        status: 'issuedCommittedPartiallyReturned',
        dateRange: 'all'
    });

    const fetchOrders = useCallback(async () => {
        setIsLoading(true);
        try {
            const response = await axios.get(`${BACKEND_URL}/api/orders/getorders/withUsers`);
            setOrders(response.data || []);
        } catch (error) {
            toast.error('Failed to fetch orders');
            console.error(error);
        } finally {
            setIsLoading(false);
        }
    }, []); // Empty dependency array as BACKEND_URL is constant and other setters don't need to be deps here

    // Initial fetch of orders
    useEffect(() => {
        fetchOrders();
    }, [fetchOrders]);


    const filteredOrders = useMemo(() => 
        filterOrders(orders, filters, debouncedSearch),
        [orders, filters, debouncedSearch]
    );


    const debouncedSetSearch = useMemo(
        () => debounce((value) => setDebouncedSearch(value), 300),
        [setDebouncedSearch]
    );


    const handleSearchChange = (e) => {
        const value = e.target.value;
        setSearchTerm(value); 
        debouncedSetSearch(value); 
    };

    const handleReturnOrder = () => {
        setIsReturnModalOpen(true);
    };

    const handleOrderSelect = async (order) => {
        if (!order.requestedByName) {
            try {
                const userResponse = await axios.get(`${BACKEND_URL}/api/users/${order.requestedBy}`);
                order.requestedByName = userResponse.data.name;
                setSelectedOrder({ ...order });
            } catch (error) {
                // Handle case where user was deleted but order still exists
                if (error.response && error.response.status === 404) {
                    order.requestedByName = 'Deleted User';
                    setSelectedOrder({ ...order });
                    toast.warning('User who requested this order has been deleted, but order can still be returned');
                } else {
                    toast.error('Failed to fetch user information');
                    console.error(error);
                    return; // Don't proceed if there's a real error
                }
            }
        } else {
            setSelectedOrder(order);
        }
    };

    const handleReturnSubmit = async (returnDataFromModal) => {
        setIsReturnModalOpen(false);
        setSelectedOrder(null); // Deselect the order
        await fetchOrders(); // Re-fetch orders to get the latest data from the backend
    };

    
return (
    <div className={styles.container}>
        <div className={styles.header}>
            <h1>Return Order</h1>
        </div>
        {isLoading ? (
            <div className={styles.loadingContainer}>
                <div className={styles.spinner}></div>
                <p>Loading data...</p>
            </div>
        ) : (
        <>

        <div className={styles.filterSection}>
            <input 
                type="text"
                 placeholder="Search by order number, name, destination, or user name..."
                value={searchTerm}
                onChange={handleSearchChange}
                className={styles.searchInput}
            />

            <select 
                value={filters.status}
                onChange={(e) => setFilters(prev => ({ ...prev, status: e.target.value }))}
                className={styles.filterSelect}
            >
                <option value="all">All Statuses</option>
                <option value="issuedCommittedPartiallyReturned">Issued & Committed & Partially Returned</option>
                <option value="issuedAndCommitted">Issued & Committed</option>
                <option value="issued">Issued</option>
                <option value="partially_returned">Partially Returned</option>
                <option value="committed">Committed</option>
            </select>

            <select 
                value={filters.dateRange}
                onChange={(e) => setFilters(prev => ({ ...prev, dateRange: e.target.value }))}
                className={styles.filterSelect}
            >
                <option value="all">All Dates</option>
                <option value="7days">Last 7 Days</option>
                <option value="30days">Last 30 Days</option>
                <option value="90days">Last 90 Days</option>
            </select>
        </div>

        <div className={styles.ordersTable}>
            <table className={styles.table}>
                <thead>
                    <tr>
                        <th className={styles.tableHeader}>Order Name</th>
                        <th className={styles.tableHeader}>Created Date</th>
                        <th className={styles.tableHeader}>Destination</th>
                        <th className={styles.tableHeader}>Requested By</th>
                        <th className={styles.tableHeader}>Status</th>
                        <th className={styles.tableHeader}>From Date</th>
                        <th className={styles.tableHeader}>Until Date</th>
                    </tr>
                </thead>
                    <tbody>
                    {filteredOrders.map(order => {
                        const isSelected = selectedOrder?._id === order._id;
                        const isOverdue  = new Date(order.untilDate) < new Date();

                        return (
                        <React.Fragment key={order._id}>
                            {/* main data row ------------------------------------------------- */}
                            <tr
                            onClick={() => handleOrderSelect(order)}
                            className={[
                                styles.tableRow,
                                isSelected && styles.selectedRow,
                                isOverdue  && styles.overdueRow
                            ].filter(Boolean).join(' ')}
                            >
                            <td className={styles.tableCell}>{order.orderName}</td>
                            <td className={styles.tableCell}>
                                {new Date(order.createdAt).toLocaleDateString('he-IL')}
                            </td>
                            <td className={styles.tableCell}>{order.destination}</td>
                            <td className={styles.tableCell}>{order.requestedByName}</td>
                            <td className={styles.tableCell}>
                                <OrderStatusBadge status={order.orderStatus} />
                            </td>
                            <td className={styles.tableCell}>
                                {new Date(order.fromDate).toLocaleDateString('he-IL')}
                            </td>
                            <td className={styles.tableCell}>
                                {new Date(order.untilDate).toLocaleDateString('he-IL')}
                            </td>
                            </tr>

                            {isSelected && order.orderStatus !== 'returned' && (
                            <tr className={styles.hoverDetailsRow}>
                                {/* use a very large colSpan so it always covers the whole table */}
                                <td colSpan={100} className={styles.hoverDetailsCell}> {/* This is the <td> */}
                                  <div className={styles.hoverDetailsCellInnerFlex}> {/* New inner flex container */}
                                    <span className={styles.descriptionText}>
                                        Order # {order.orderNumber}
                                    </span>

                                    <button
                                        onClick={handleReturnOrder}
                                        className={styles.returnButton}
                                    >
                                        Return&nbsp;Order
                                    </button>
                                  </div>
                                </td>
                            </tr>
                            )}
                        </React.Fragment>
                        );
                    })}
                    </tbody>

            </table>
        </div>


        {selectedOrder && isReturnModalOpen && (
            <ReturnOrderDetail 
                order={selectedOrder}
                onClose={() => setIsReturnModalOpen(false)}
                onSubmit={handleReturnSubmit}
            />
        )}
        </>
        )}
    </div>
    );
};

export default ReturnOrder;
